import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import AsyncSelect from 'react-select/async';
import { TextField, Checkbox, Box, Text, Spinner, InlineStack, Badge, DataTable, Button, Tooltip } from '@shopify/polaris';
import { getRequest, formatToCurrency } from '../functions';

export default function AdvancedProductSearchModal({ active, setActive, onAddProducts, disabled }) {
  // Filter state
  const [supplier, setSupplier] = useState(null);
  const [supplierSelectValue, setSupplierSelectValue] = useState(null);
  const [productRef, setProductRef] = useState('');
  const [productName, setProductName] = useState('');
  const [includeDiscontinued, setIncludeDiscontinued] = useState(false);

  // Results state
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Cart state - products accumulated across multiple searches
  const [cart, setCart] = useState([]);

  // Debounce timer
  const debounceTimer = useRef(null);

  // Modal container ref for dropdown portals
  const modalRef = useRef(null);

  // Load supplier options for AsyncSelect
  const loadSupplierOptions = useCallback(async (inputValue) => {
    if (!inputValue) return [];
    try {
      const data = await getRequest(`/api/companies/simple/search?q=NAAM:${inputValue}`);
      return data.map((company) => {
        const street = company.STRAAT_NR ? `${company.STRAAT_NR},` : '';
        const address = company.STRAAT_NR || company.POSTNR || company.WOONPL ? `(${street} ${company.POSTNR || ''} ${company.WOONPL || ''})` : '';
        return {
          value: company.FIRNR,
          label: `${company.NAAM} ${address}`
        };
      });
    } catch (error) {
      console.error('Error loading suppliers:', error);
      return [];
    }
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!active) {
      setSupplier(null);
      setSupplierSelectValue(null);
      setProductRef('');
      setProductName('');
      setIncludeDiscontinued(false);
      setSearchResults([]);
      setHasSearched(false);
      setCart([]);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    }
  }, [active]);

  const handleSearch = useCallback(async () => {
    setIsSearching(true);
    setHasSearched(true);

    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (supplier) params.append('supplier', supplier);
      if (productRef.trim()) params.append('productRef', productRef.trim());
      if (productName.trim()) params.append('productName', productName.trim());
      if (includeDiscontinued) params.append('includeDiscontinued', 'true');

      const results = await getRequest(`/api/products/advanced-search?${params.toString()}`);
      setSearchResults(results || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [supplier, productRef, productName, includeDiscontinued]);

  // Auto-search when filters change (with debounce)
  useEffect(() => {
    if (!active) return;

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounced search (500ms delay)
    debounceTimer.current = setTimeout(() => {
      handleSearch();
    }, 500);

    // Cleanup on unmount
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [supplier, productRef, productName, includeDiscontinued, active, handleSearch]);

  const handleToggleProduct = useCallback(
    (product) => {
      // Check if product is already in cart
      const isInCart = cart.some((p) => p.ProductID === product.ProductID);

      if (isInCart) {
        // Remove from cart
        setCart((prevCart) => prevCart.filter((p) => p.ProductID !== product.ProductID));
      } else {
        // Add to cart
        setCart((prevCart) => [...prevCart, product]);
      }
    },
    [cart]
  );

  const handleToggleAll = useCallback(() => {
    const allInCart = searchResults.every((product) => cart.some((p) => p.ProductID === product.ProductID));

    if (allInCart) {
      // Remove all search results from cart
      setCart((prevCart) => prevCart.filter((p) => !searchResults.some((sr) => sr.ProductID === p.ProductID)));
    } else {
      // Add all search results to cart (avoiding duplicates)
      setCart((prevCart) => {
        const existingIds = new Set(prevCart.map((p) => p.ProductID));
        const newProducts = searchResults.filter((p) => !existingIds.has(p.ProductID));
        return [...prevCart, ...newProducts];
      });
    }
  }, [searchResults, cart]);

  const handleRemoveFromCart = useCallback((productId) => {
    setCart((prevCart) => prevCart.filter((p) => p.ProductID !== productId));
  }, []);

  const handleAddAllToLines = useCallback(() => {
    if (cart.length > 0) {
      onAddProducts(cart);
      setActive(false);
    }
  }, [cart, onAddProducts, setActive]);

  const renderProductNameCell = (productName) => {
    if (!productName) return '-';

    return (
      <Tooltip content={productName} preferredPosition="above" width="wide" zIndexOverride={1000002}>
        <span
          style={{
            display: 'inline-block',
            maxWidth: '300px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            verticalAlign: 'middle',
            whiteSpace: 'nowrap'
          }}
        >
          {productName}
        </span>
      </Tooltip>
    );
  };

  // Prepare table rows
  const rows = searchResults.map((product) => {
    const isInCart = cart.some((p) => p.ProductID === product.ProductID);
    return [
      <Checkbox checked={isInCart} onChange={() => handleToggleProduct(product)} />,
      product.ProductID || '-',
      product.Productref || '-',
      renderProductNameCell(product.ProductNameNL),
      product.SupplierName || '-',
      product.SalesPrice ? formatToCurrency(product.SalesPrice) : '-',
      product.Discontinued ? (
        <Badge tone="warning" size="small">
          Discontinued
        </Badge>
      ) : (
        <Badge tone="success" size="small">
          Active
        </Badge>
      )
    ];
  });

  const allInCart = searchResults.length > 0 && searchResults.every((product) => cart.some((p) => p.ProductID === product.ProductID));
  const headings = [<Checkbox checked={allInCart} onChange={handleToggleAll} />, <b>Code</b>, <b>Ref</b>, <b>Product Name</b>, <b>Supplier</b>, <b>Sales Price</b>, <b>Status</b>];

  if (!active) return null;

  return createPortal(
    <>
      <style>{`
        /* Fix react-select dropdown in portal - must be higher than modal */
        #react-select-CompanySelect-listbox,
        [id^="react-select"][id$="-listbox"],
        [id*="CompanySelect"],
        div[class*="menu"],
        div[class*="MenuList"],
        div[class*="menuPortal"] {
          z-index: 10000000 !important;
        }

        /* Ensure the menu portal container is also high z-index */
        body > div[class*="menuPortal"] {
          z-index: 10000000 !important;
        }
      `}</style>
      <div
        className="advanced-search-modal-open"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }}
        onClick={(e) => {
          // Prevent closing when clicking backdrop
          e.stopPropagation();
        }}
      >
        <div
          ref={modalRef}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '95vw',
            maxWidth: '1800px',
            height: '90vh',
            maxHeight: '1200px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 26px 80px rgba(0, 0, 0, 0.5)',
            position: 'relative',
            zIndex: 1000000
          }}
          onClick={(e) => {
            // Prevent clicks inside modal from bubbling to backdrop
            e.stopPropagation();
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e1e3e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Text variant="headingLg" as="h2">
              Advanced Product Search
            </Text>
            <Button onClick={() => setActive(false)} plain>
              ✕
            </Button>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
            <Box paddingBlockEnd="400">
              <InlineStack gap="300" wrap={false} blockAlign="end">
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: '4px' }}>
                    <Text variant="bodyMd" as="span" fontWeight="medium">
                      Supplier
                    </Text>
                  </div>
                  <AsyncSelect
                    value={supplierSelectValue}
                    loadOptions={loadSupplierOptions}
                    isClearable
                    isDisabled={disabled}
                    placeholder="Search supplier..."
                    menuPortalTarget={document.body}
                    autoFocus
                    openMenuOnFocus
                    autoComplete="nope"
                    inputProps={{ autoComplete: 'nope', 'data-form-type': 'other', 'data-lpignore': 'true' }}
                    styles={{
                      valueContainer: (base) => ({ ...base, height: '32px' }),
                      indicatorSeparator: (base) => ({ ...base, height: '32px' }),
                      indicatorsContainer: (base) => ({ ...base, height: '32px' }),
                      control: (base) => ({ ...base, height: '32px', minHeight: '32px', borderRadius: '8px', borderColor: disabled ? '#ebebeb' : '#919191' }),
                      menu: (provided) => ({ ...provided, zIndex: 10000001 }),
                      menuPortal: (base) => ({ ...base, zIndex: 10000001 })
                    }}
                    defaultOptions
                    onChange={(selected) => {
                      setSupplierSelectValue(selected);
                      setSupplier(selected ? selected.value : null);
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <TextField label="Product Name" value={productName} onChange={setProductName} autoComplete="off" disabled={disabled} />
                </div>
                <div style={{ flex: 1 }}>
                  <TextField label="Product Ref" value={productRef} onChange={setProductRef} autoComplete="off" disabled={disabled} />
                </div>
                <div style={{ paddingBottom: '2px' }}>
                  <Checkbox label="Include discontinued" checked={includeDiscontinued} onChange={setIncludeDiscontinued} disabled={disabled} />
                </div>
              </InlineStack>
            </Box>

            <Box paddingBlockStart="400">
              <div style={{ minHeight: '500px' }}>
                {isSearching && (
                  <Box paddingBlock="600">
                    <InlineStack align="center" gap="200">
                      <Spinner size="small" />
                      <Text variant="bodyMd" as="span">
                        Searching products...
                      </Text>
                    </InlineStack>
                  </Box>
                )}

                {!isSearching && hasSearched && searchResults.length === 0 && (
                  <Box paddingBlock="600">
                    <Text variant="bodyMd" as="p" alignment="center" tone="subdued">
                      No products found. Try adjusting your filters.
                    </Text>
                  </Box>
                )}

                {!isSearching && !hasSearched && (
                  <Box paddingBlock="600">
                    <Text variant="bodyMd" as="p" alignment="center" tone="subdued">
                      Start typing to search for products.
                    </Text>
                  </Box>
                )}

                {!isSearching && searchResults.length > 0 && (
                  <>
                    <div style={{ marginBottom: '8px' }}>
                      <Text variant="bodyMd" as="p" fontWeight="semibold">
                        Found {searchResults.length} product{searchResults.length !== 1 ? 's' : ''}
                      </Text>
                    </div>
                    <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                      <DataTable columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'text']} headings={headings} rows={rows} stickyHeader increasedTableDensity />
                    </div>
                  </>
                )}
              </div>
            </Box>

            {/* Cart Section */}
            {cart.length > 0 && (
              <Box paddingBlockStart="400">
                <div style={{ borderTop: '2px solid #e1e3e5', paddingTop: '16px' }}>
                  <Text variant="headingMd" as="h3">
                    Selected Products ({cart.length})
                  </Text>
                  <div style={{ marginTop: '12px', maxHeight: '200px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                          <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600 }}>Code</th>
                          <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600 }}>Ref</th>
                          <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600 }}>Name</th>
                          <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600 }}>Supplier</th>
                          <th style={{ textAlign: 'right', padding: '8px', fontWeight: 600 }}>Price</th>
                          <th style={{ width: '40px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {cart.map((product) => (
                          <tr key={product.ProductID} style={{ borderBottom: '1px solid #f1f1f1' }}>
                            <td style={{ padding: '8px' }}>{product.ProductID}</td>
                            <td style={{ padding: '8px' }}>{product.Productref || '-'}</td>
                            <td style={{ padding: '8px' }}>{product.ProductNameNL || '-'}</td>
                            <td style={{ padding: '8px' }}>{product.SupplierName || '-'}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>{product.SalesPrice ? formatToCurrency(product.SalesPrice) : '-'}</td>
                            <td style={{ padding: '8px' }}>
                              <Button size="slim" onClick={() => handleRemoveFromCart(product.ProductID)} plain destructive>
                                ✕
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Box>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid #e1e3e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}
          >
            <div>
              {cart.length > 0 && (
                <Text variant="bodyMd" as="span" tone="subdued">
                  {cart.length} product{cart.length !== 1 ? 's' : ''} ready to add
                </Text>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <Button onClick={() => setActive(false)}>Cancel</Button>
              <Button primary onClick={handleAddAllToLines} disabled={cart.length === 0 || disabled}>
                Add All to Lines ({cart.length})
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
